import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { EnmFormData } from './EnmInterfaces';

// Validation schema for ENM
const enmSchema = yup.object().shape({
  // ENM Information
  name: yup
    .string()
    .required('ENM name is required')
    .min(3, 'ENM name must be at least 3 characters')
    .max(100, 'ENM name must not exceed 100 characters'),

  category: yup
    .string()
    .required('Environment category is required')
    .oneOf(['Production', 'Staging', 'Development', 'Testing'], 'Invalid category'),

  description: yup
    .string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description must not exceed 500 characters'),

  // Connection Details
  url: yup
    .string()
    .required('URL is required')
    .url('Please enter a valid URL')
    .matches(/^https?:\/\//, 'URL must start with http:// or https://'),

  // Credentials (required but no validation rules)
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),

  // SFTP Configuration
  sftpIp: yup
    .string()
    .required('SFTP IP is required')
    .matches(
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
      'Please enter a valid IPv4 address'
    ),

  sftpPath: yup
    .string()
    .required('SFTP path is required')
    .min(1, 'SFTP path must be at least 1 character')
    .matches(/^\//, 'SFTP path must start with /'),

  // TLS Certificate
  tlsCertificate: yup
    .string()
    .required('TLS certificate is required')
    .matches(
      /-----BEGIN CERTIFICATE-----[\s\S]*-----END CERTIFICATE-----/,
      'Certificate must contain BEGIN CERTIFICATE and END CERTIFICATE blocks'
    ),

  // Linked EIAP Clusters (optional)
  linkedEiapClusters: yup.array().of(yup.string().required()).default([]),
});

export type EnmFormValues = yup.InferType<typeof enmSchema>;

export const useEnmForm = (initialValues?: Partial<EnmFormData>) => {
  const defaultValues: Partial<EnmFormData> = {
    name: '',
    category: 'Development',
    description: '',
    url: '',
    username: '',
    password: '',
    sftpIp: '',
    sftpPath: '',
    tlsCertificate: '',
    linkedEiapClusters: [],
    ...initialValues,
  };

  return useForm({
    resolver: yupResolver(enmSchema) as any,
    defaultValues,
    mode: 'onChange',
  }) as any;
};
